import React from 'react';

// Spacer ring visualization component - handles grouped allocations
export const SpacerRingCombo = ({ groups }) => {
  // Color mapping for different spacer sizes
  const getSpacerStyle = (size) => {
    const styles = {
      20: 'bg-rose-500 text-white',
      19: 'bg-amber-500 text-white',
      11: 'bg-emerald-500 text-white',
      9: 'bg-cyan-500 text-white',
      5: 'bg-violet-500 text-white',
    };
    return styles[size] || 'bg-gray-500 text-white';
  };
  
  // Width proportional to size (min 18px, scale factor ~1.5)
  const getWidth = (size) => Math.max(18, size * 1.5);
  
  if (!groups || groups.length === 0) {
    return <span className="text-[10px] text-gray-400">-</span>;
  }
  
  // Convert counts to spacer array for display
  const countsToSpacers = (counts) => {
    const spacers = [];
    for (const [size, count] of Object.entries(counts)) {
      for (let i = 0; i < count; i++) {
        spacers.push(Number(size));
      }
    }
    return spacers.sort((a, b) => b - a);
  };
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      {groups.map((group, groupIdx) => (
        <div key={`grp-${groupIdx}-${Object.entries(group.counts || {}).map(([s, c]) => `${s}x${c}`).join('.')}`} className="flex items-center gap-0.5">
          {countsToSpacers(group.counts).map((size, idx) => (
            <div
              key={`sp-${idx}-${size}`}
              className={`${getSpacerStyle(size)} text-[9px] sm:text-[10px] font-bold flex items-center justify-center rounded-sm border border-white/30`}
              style={{ 
                width: `${getWidth(size)}px`, 
                height: '20px',
                minWidth: '18px'
              }}
            >
              {size}
            </div>
          ))}
          {group.remainder > 0 && (
            <div 
              className="bg-red-200 text-red-800 text-[9px] sm:text-[10px] font-bold flex items-center justify-center rounded-sm border border-red-400"
              style={{ width: '28px', height: '20px' }}
              title={`Missing: ${group.remainder}mm`}
            >
              +{group.remainder}
            </div>
          )}
          {group.count > 1 && (
            <span className="text-[9px] text-gray-500 ml-0.5">×{group.count}</span>
          )}
          {groupIdx < groups.length - 1 && (
            <span className="text-gray-300 mx-1">|</span>
          )}
        </div>
      ))}
    </div>
  );
};

// Single tube row component
// Right tube is fixed width so right edges align vertically across all stations
export const TubeRow = ({ 
  children,
  className = ""
}) => (
  <div className="py-0.5">
    <div className={`flex items-center w-full ${className}`}>
      <div className="flex-1 h-6 sm:h-7 bg-gradient-to-r from-slate-400 via-slate-500 to-slate-400 rounded-l-full border-2 border-slate-600 border-r-0 shadow-inner"></div>
      <div className="flex items-center h-6 sm:h-7" style={{ overflow: 'visible' }}>
        {children}
      </div>
      <div className="w-6 sm:w-8 h-6 sm:h-7 bg-gradient-to-r from-slate-400 via-slate-500 to-slate-400 rounded-r-full border-2 border-slate-600 border-l-0 shadow-inner flex-shrink-0"></div>
    </div>
  </div>
);

// Yellow gap element - float number above when too small
export const GapElement = ({ width, value, hideValue = false }) => {
  const fitsInside = width >= 30;
  return (
    <div 
      className={`bg-gradient-to-b from-yellow-200 to-yellow-300 border-y-2 border-yellow-500 flex items-center justify-center flex-shrink-0 box-border relative ${fitsInside ? 'overflow-hidden' : ''}`}
      style={{ width: `${width}px`, height: '100%', minHeight: '24px' }}
    >
      {hideValue ? null : fitsInside ? (
        <span className="text-[10px] sm:text-xs font-bold text-yellow-800">{fmt(value)}</span>
      ) : (
        <span 
          className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 text-[11px] sm:text-xs font-bold whitespace-nowrap pointer-events-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
          style={{ color: '#b8860b' }}
        >
          {fmt(value)}
        </span>
      )}
    </div>
  );
};

// Tube extension (for alignment spacers)
export const TubeExtension = ({ width }) => {
  return (
    <div 
      className="bg-gradient-to-r from-slate-400 via-slate-500 to-slate-400 border-y-2 border-slate-600 shadow-inner flex-shrink-0"
      style={{ width: `${width}px`, height: '100%', minHeight: '24px' }}
    ></div>
  );
};

// Format number: show .5 when needed, drop .0
const fmt = (v) => v % 1 === 0 ? v.toFixed(0) : v.toFixed(1);

// Fill distance element - shows how many mm to fill from the right starting point
export const FillElement = ({ width, value }) => (
  <div 
    className="h-full bg-gradient-to-br from-zinc-400 to-zinc-500 border-zinc-600 border-y-2 rounded font-bold text-white text-[10px] sm:text-sm shadow-lg flex items-center justify-center flex-shrink-0 whitespace-nowrap overflow-hidden"
    style={{ width: `${width}px`, minWidth: 0, padding: width >= 30 ? undefined : 0 }}
  >
    {width >= 30 && fmt(value)}
  </div>
);

// Static fill block - dark, no number (for fixed 110/111mm on Station 5-10)
export const StaticFill = ({ width, extra }) => (
  <div 
    className="bg-zinc-950 border-y-2 border-zinc-800 flex items-center flex-shrink-0 box-border"
    style={{ width: `${width}px`, height: '100%', minHeight: '24px' }}
  >
    {extra > 0 && (
      <span className="ml-1.5 text-[9px] sm:text-[10px] font-bold text-zinc-500">+{fmt(extra)}</span>
    )}
  </div>
);

// 10mm end plate - flat stainless steel, centered on tube (equal above and below)
export const PlateElement = ({ scale, compact }) => {
  const plateHeight = compact ? 40 : 72;
  return (
    <div 
      className="flex-shrink-0 box-border self-center"
      style={{ 
        width: `${10 * scale}px`, 
        height: `${plateHeight}px`,
        background: '#c8c8c8',
        borderLeft: '1.5px solid #a0a0a0',
        borderRight: '1.5px solid #a0a0a0',
        borderTop: '2px solid #d8d8d8',
        borderBottom: '2px solid #999',
        borderRadius: '2px',
      }}
    />
  );
};

// Plated spacer box - 10mm plates on each end, shows middle value (for all top tubes)
export const PlatedSpacerBox = ({ width, value, colorClass, scale, compact }) => {
  const plateWidth = 10 * scale;
  const middleValue = value - 20;
  const middleWidth = Math.max(width - (2 * plateWidth), 0);
  const fitsInside = middleWidth >= 30;
  
  // No middle section when value is 0
  if (middleWidth <= 0) {
    return (
      <>
        <PlateElement scale={scale} compact={compact} />
        <PlateElement scale={scale} compact={compact} />
      </>
    );
  }
  
  return (
    <>
      <PlateElement scale={scale} compact={compact} />
      <div 
        className={`h-full ${colorClass} border-y-2 font-bold text-white text-[10px] sm:text-sm shadow-lg flex items-center justify-center flex-shrink-0 whitespace-nowrap relative ${fitsInside ? 'overflow-hidden' : ''}`}
        style={{ width: `${middleWidth}px`, minWidth: 0, padding: 0 }}
      >
        {fitsInside ? fmt(middleValue) : (
          <span 
            className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 text-[11px] sm:text-xs font-bold whitespace-nowrap pointer-events-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
            style={{ color: '#ff9d00' }}
          >
            {fmt(middleValue)}
          </span>
        )}
      </div>
      <PlateElement scale={scale} compact={compact} />
    </>
  );
};

// Spacer box element
export const SpacerBox = ({ width, value, colorClass }) => (
  <div 
    className={`h-full ${colorClass} border-y-2 rounded font-bold text-white text-[10px] sm:text-sm shadow-lg flex items-center justify-center flex-shrink-0 whitespace-nowrap overflow-hidden`}
    style={{ width: `${width}px`, minWidth: 0, padding: width >= 30 ? undefined : 0 }}
  >
    {width >= 30 && fmt(value)}
  </div>
);

// Side Rolling Station (Stations 5-10)
